import React, { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Pre-launch splash screen.
 *
 *   - Fullscreen looping 10-second video with a transparent clickable hotspot
 *     positioned precisely over the DOOZYGO logo in the top-left of the frame.
 *   - Autoplay is attempted unmuted; browsers that block unmuted autoplay fall
 *     back to muted-autoplay and surface a mute/unmute toggle (top-right).
 *   - A static poster image is shown if the <video> fails to load at all.
 *   - Keyboard-accessible: the hotspot is a real <button>, Tab-focusable,
 *     Enter/Space triggers entry. aria-label is set.
 *   - The hotspot's position is kept stable across devices by locking the
 *     video into a 16:9 aspect-ratio stage (letterboxed on mobile portrait),
 *     so the % coordinates always align with the real logo location.
 *
 *  Props:
 *    onEnter  — called when the visitor clicks/activates the logo hotspot.
 */
const SplashScreen = ({ onEnter }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  // Try to autoplay with sound. If the browser rejects (Safari/iOS/Chrome
  // without user gesture), silently fall back to muted autoplay.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    const tryPlay = async () => {
      try {
        await v.play();
      } catch {
        try {
          v.muted = true;
          setMuted(true);
          await v.play();
        } catch {
          setAutoplayBlocked(true);
        }
      }
    };
    tryPlay();
  }, []);

  const toggleMute = useCallback((e) => {
    e?.stopPropagation?.();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const handleEnter = useCallback(() => {
    try {
      const v = videoRef.current;
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    } catch (_) { /* ignore */ }
    onEnter?.();
  }, [onEnter]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleEnter();
      }
    },
    [handleEnter]
  );

  // One-click fallback when autoplay is blocked — user tap starts video.
  const handleStageTap = useCallback(() => {
    if (!autoplayBlocked) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    setMuted(true);
    v.play().then(() => setAutoplayBlocked(false)).catch(() => {});
  }, [autoplayBlocked]);

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #ffe6ef 0%, #ffd9ec 25%, #e9d5ff 55%, #d4e4ff 85%, #ffeaf3 100%)",
      }}
      data-testid="splash-screen"
      role="dialog"
      aria-label="DoozyGo pre-launch — click the logo to enter the website"
      onClick={handleStageTap}
    >
      {/* 16:9 stage sized to COVER the viewport on desktop/landscape (any
          excess is cropped on the right/bottom). On narrow portrait phones
          the "cover" stage would push the logo far off-screen, so a media
          query falls back to letterboxing the video horizontally — the
          complete frame (incl. the GO hotspot) stays visible, with the pink
          gradient filling the top/bottom. Desktop styling is untouched. */}
      <div
        className="absolute splash-stage"
        style={{
          width: "max(100vw, calc(100vh * 16 / 9))",
          height: "max(100vh, calc(100vw * 9 / 16))",
          top: 0,
          left: 0,
        }}
      >
        {!videoFailed ? (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full"
            autoPlay
            loop
            playsInline
            preload="auto"
            onError={() => setVideoFailed(true)}
            data-testid="splash-video"
          >
            <source src="/launch/splash.webm" type="video/webm" />
            <source src="/launch/splash.mp4" type="video/mp4" />
          </video>
        ) : (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center text-center p-6">
            <div>
              <img
                src="/doozygo-logo.png"
                alt="DoozyGo"
                className="mx-auto mb-6 h-24 w-auto"
              />
              <h1 className="text-2xl md:text-4xl font-black text-[#1E1B4B] mb-2">
                We're Almost Live!
              </h1>
              <p className="text-sm md:text-base text-[#475569] max-w-md mx-auto">
                Something exciting is on the way. Tap the GO badge to enter the
                site.
              </p>
            </div>
          </div>
        )}

        {/* Invisible circular click target sitting over ONLY the "GO" badge
            inside the DoozyGO logo. No visible outline, no pulse — only the
            internal team knows where to click. Hover reveals a very faint
            glow so a trusted user can confirm they're on the right spot. */}
        <button
          type="button"
          onClick={handleEnter}
          onKeyDown={handleKeyDown}
          aria-label="Enter DOOZYGO website"
          className="group absolute outline-none focus-visible:ring-4 focus-visible:ring-white/40"
          style={{
            left: "26%",
            top: "10%",
            width: "3%",
            height: "4%",
            borderRadius: "50%",
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
          data-testid="splash-logo-hotspot"
        >
          <span
            className="absolute inset-0 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-300"
            style={{
              boxShadow: "0 0 0 2px rgba(255,215,0,0.55), 0 0 24px 4px rgba(255,215,0,0.35)",
            }}
            aria-hidden="true"
          />
        </button>
      </div>

        {/* Mute / Unmute toggle — only shown while the video is actually playing. */}
        {!videoFailed && (
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute splash video" : "Mute splash video"}
            className="absolute right-4 top-4 md:right-6 md:top-6 w-11 h-11 rounded-full flex items-center justify-center text-white backdrop-blur-md transition-all hover:scale-105 focus-visible:ring-4 focus-visible:ring-white/40 outline-none"
            style={{
              background: "rgba(30,27,75,0.55)",
              border: "1px solid rgba(255,255,255,0.25)",
            }}
            data-testid="splash-mute-toggle"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        )}

        {/* Autoplay-blocked hint (iOS/Safari sometimes blocks even muted
            autoplay until the user interacts). */}
        {autoplayBlocked && !videoFailed && (
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full text-white text-sm font-semibold backdrop-blur"
            style={{ background: "rgba(30,27,75,0.65)" }}
            data-testid="splash-tap-to-play"
          >
            Tap anywhere to start
          </div>
        )}

      {/* Local keyframes + responsive portrait override (mobile only). */}
      <style>{`
        @keyframes splashPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        /* On narrow portrait phones, letterbox the 16:9 video horizontally
           so the GO hotspot stays on-screen. Desktop/landscape remains
           full-cover (unchanged). */
        @media (max-width: 767px) and (orientation: portrait) {
          .splash-stage {
            width: 100vw !important;
            height: calc(100vw * 9 / 16) !important;
            top: 50% !important;
            left: 0 !important;
            transform: translateY(-50%);
          }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
